/* eslint-disable yi/avoid-relative-parent-imports, camelcase */
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Query_CurrentUser_CurrentUser } from '../gql/generated/typesClient/Query_CurrentUser';
import { ParsedUserJwt, parseUserJwt, shouldRenew } from './parseUserJwt';
import { BrooklynResponses } from '../types/brooklyn';
import { runWithRetry } from '../runWithRetry';

export const USER_JWT_KEY = 'yi.brooklynUserJwt';

type StorageSchema = {
  [USER_JWT_KEY]: string;
};

interface AsyncStorage<S extends StorageSchema, K extends Extract<keyof S, string>> {
  getItem: (k: K) => Promise<S[K] | null | undefined>;
  setItem: (k: K, v: S[K]) => Promise<void>;
  removeItem: (k: K) => Promise<void>;
}

type CurrentUser = Pick<
  Query_CurrentUser_CurrentUser,
  'gaia_id' | 'subscription_gateway_shortname' | 'membership_tier'
>;

type RenewResponse = BrooklynResponses['/v1/renew'];
interface Args {
  asyncStorage: AsyncStorage<StorageSchema, keyof StorageSchema>;
  fetchUserJwt: () => Promise<string>;
  renewUserJwt: (jwt: string) => Promise<RenewResponse>;
  currentUser: null | CurrentUser;
  currentUserLoading: boolean;
  onError: (e: Error) => void;
}

const opts = {
  maxRetries: 3,
  onError: (e: Error, retryCount: number) => console.warn(`Retry:${retryCount} - ${e}`),
};

const fiveMinutes = 5 * 60 * 1000;

const getJwtAndUpdateStorage = async ({
  renewUserJwt,
  fetchUserJwt,
  asyncStorage,
  currentUser,
  forceRenew,
}: Omit<Args, 'currentUser' | 'currentUserLoading' | 'onError'> & {
  currentUser: CurrentUser;
  forceRenew?: boolean;
}) => {
  let jwt = await asyncStorage.getItem(USER_JWT_KEY);

  // Check if pre-existing JWT is valid
  if (jwt) {
    try {
      const parsed = parseUserJwt(jwt);
      if (parsed.uuid !== currentUser.gaia_id) {
        throw new Error('User ID does not match JWT');
      }
    } catch (e) {
      await asyncStorage.removeItem(USER_JWT_KEY); // clear invalid JWT
      jwt = null;
    }
  }

  // Renew JWT if needed
  if (jwt && (forceRenew || shouldRenew(jwt))) {
    const refreshRes = await runWithRetry(async () => jwt && renewUserJwt(jwt), opts);
    if (refreshRes && 'jwt' in refreshRes && refreshRes.jwt) {
      jwt = refreshRes.jwt;
      await asyncStorage.setItem(USER_JWT_KEY, jwt);
    } else {
      // Renew failed, clear existing and attempt to fetch new JWT below
      jwt = null;
      await asyncStorage.removeItem(USER_JWT_KEY);
    }
  }

  // Fetch JWT if missing or invalid
  if (!jwt) {
    jwt = await runWithRetry(fetchUserJwt, opts);
    parseUserJwt(jwt);
    if (jwt) await asyncStorage.setItem(USER_JWT_KEY, jwt);
  }

  if (!jwt) throw new Error('Missing JWT');

  return { jwt, parsedJwt: parseUserJwt(jwt) };
};

export const useBrooklynUserJwt = ({
  renewUserJwt,
  fetchUserJwt,
  asyncStorage,
  currentUser,
  currentUserLoading,
  onError,
}: Args) => {
  const [result, setResult] = useState<
    { jwt: string; parsedJwt: ParsedUserJwt } | { error: string } | null
  >(null);
  const [loading, setLoading] = useState(true); // initial loading only
  const [checkCount, setCheckCount] = useState(0); // value incremented to force JWT check

  // Check JWT, renew if necessary
  useEffect(() => {
    const intervalId = setInterval(() => setCheckCount(n => n + 1), fiveMinutes);
    return () => clearInterval(intervalId);
  }, []);

  // State should be set to loading again if a new user is detected, otherwise code written to
  // check for a missing JWT after loading has finished will produce false positives
  const gaiaId = currentUser?.gaia_id || null;
  const lastGaiaIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (gaiaId && lastGaiaIdRef.current === null) setLoading(true);
    lastGaiaIdRef.current = gaiaId;
  }, [gaiaId]);

  // If JWT in storage, use it. If not, fetch it. If expired, refresh it.
  useEffect(() => {
    (async () => {
      if (currentUserLoading) return; // wait for user

      // Skip JWT for anonymous users
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const res = await getJwtAndUpdateStorage({
          renewUserJwt,
          fetchUserJwt,
          asyncStorage,
          currentUser,
        });
        setResult(res);
      } catch (e) {
        if (onError) onError(e instanceof Error ? e : new Error(`${e}`));
        setResult({ error: `${e}` });
      }

      setLoading(false);
    })();
  }, [
    currentUser,
    currentUserLoading,
    checkCount,
    renewUserJwt,
    fetchUserJwt,
    asyncStorage,
    onError,
  ]);

  const forceRenewUserJwt = useCallback(
    async (_currentUser: CurrentUser) => {
      if (!_currentUser) {
        return null;
      }

      return getJwtAndUpdateStorage({
        forceRenew: true,
        renewUserJwt,
        fetchUserJwt,
        asyncStorage,
        currentUser: _currentUser,
      });
    },
    [renewUserJwt, fetchUserJwt, asyncStorage],
  );

  // Always renew on initial load
  const didRenewRef = useRef(false);
  useEffect(() => {
    if (currentUser && !didRenewRef.current) {
      forceRenewUserJwt(currentUser);
      didRenewRef.current = true; // ensure only runs once
    }
  }, [forceRenewUserJwt, currentUser]);

  return useMemo(
    () => ({
      loading,
      error: result && 'error' in result ? result.error : null,
      jwt: result && 'jwt' in result ? result.jwt : null,
      parsedJwt: result && 'jwt' in result ? result.parsedJwt : null,
      forceRenewUserJwt,
    }),
    [loading, result, forceRenewUserJwt],
  );
};
